import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Heart, Award, ArrowRight, Sparkles, Search, Globe } from 'lucide-react';
import SEO from '../components/SEO';

export default function AboutPage() {
    const values = [
        {
            name: 'Unify',
            icon: Heart,
            color: '#06b6d4',
            description: 'Creating a cohesive bridge across the fragmented cultural landscape'
        },
        {
            name: 'Amplify',
            icon: Sparkles,
            color: '#f43f5e',
            description: 'Strengthening the collective reach of our partner organizations'
        },
        {
            name: 'Simplify',
            icon: Search,
            color: '#3b82f6',
            description: 'Making cultural discovery accessible through one streamlined platform'
        },
    ];

    const team = [
        {
            role: 'Our Mission',
            icon: Target,
            color: '#06b6d4',
            description: 'To create a unified platform that celebrates and promotes Singapore\'s Indian Heritage and Arts, making it accessible to everyone while empowering our partner organizations.'
        },
        {
            role: 'Our Vision',
            icon: Globe,
            color: '#f43f5e',
            description: 'To be the leading cultural hub for Singapore\'s Indian Heritage and Arts, fostering a vibrant community where tradition meets innovation and heritage thrives.'
        },
        {
            role: 'Our Community',
            icon: Users,
            color: '#a855f7',
            description: 'We bring together artists, organizations, and audiences to create a thriving ecosystem that celebrates Singapore\'s Indian Heritage and Arts across generations.'
        }
    ];

    return (
        <main className="main">
            <SEO 
                title="About Us"
                description="Learn about ATAM Connect, a digital gateway to Singapore's traditional folk arts, bringing together events, workshops and resources that celebrate Indian and multicultural heritage."
                keywords="about ATAM, Indian heritage Singapore, cultural platform, traditional folk arts, Singapore arts community"
            />
            {/* Hero Section */}
            <section className="hero" style={{
                minHeight: '400px',
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h1 style={{ fontSize: '48px', marginBottom: '24px' }}>About ATAM Connect</h1>
                    <p style={{
                        fontSize: '20px',
                        maxWidth: '800px',
                        margin: '0 auto',
                        color: '#212121',
                        lineHeight: '1.6'
                    }}>
                        ATAM Connect is a digital gateway to Singapore's traditional folk arts, bringing together 
                        events, workshops and resources that celebrate Indian and multicultural heritage. It connects 
                        artists, communities and audiences on one accessible platform, making it easier to discover, 
                        experience and support living traditions across Singapore.
                    </p>
                </div>
            </section>

            {/* Mission, Vision, Community */}
            <section style={{ padding: '80px 0' }}>
                <div className="container">
                    <h2 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        marginBottom: '48px',
                        textAlign: 'center'
                    }}>
                        Who We Are
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '32px'
                    }}>
                        {team.map((item) => (
                            <div
                                key={item.role}
                                className="card"
                                style={{
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'var(--bg-card)',
                                    border: `2px solid ${item.color}20`,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: `${item.color}20`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px'
                                }}>
                                    <item.icon size={32} style={{ color: item.color }} />
                                </div>
                                <h3 style={{
                                    fontSize: '24px',
                                    fontWeight: '600',
                                    color: item.color,
                                    marginBottom: '12px'
                                }}>
                                    {item.role}
                                </h3>
                                <p style={{
                                    color: 'var(--text-secondary)',
                                    fontSize: '15px',
                                    lineHeight: '1.7'
                                }}>
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
                <div className="container">
                    <h2 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        marginBottom: '16px',
                        textAlign: 'center'
                    }}>
                        Our Values
                    </h2>
                    <p style={{
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        marginBottom: '48px',
                        fontSize: '16px'
                    }}>
                        The principles that guide everything we do
                    </p>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '32px'
                    }}>
                        {values.map((value) => (
                            <div
                                key={value.name}
                                className="card"
                                style={{
                                    padding: '32px',
                                    textAlign: 'center',
                                    background: 'var(--bg-card)',
                                }}
                            >
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: `${value.color}20`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <value.icon size={28} style={{ color: value.color }} />
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: value.color,
                                    marginBottom: '8px'
                                }}>
                                    {value.name}
                                </h3>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section style={{ padding: '80px 0' }}>
                <div className="container">
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <h2 style={{
                            fontSize: '36px',
                            fontWeight: '700',
                            marginBottom: '24px',
                            textAlign: 'center'
                        }}>
                            Our Story
                        </h2>
                        <div style={{
                            color: 'var(--text-secondary)',
                            fontSize: '16px',
                            lineHeight: '1.8',
                            textAlign: 'center'
                        }}>
                            <p style={{ marginBottom: '20px' }}>
                                ATAM Connect was born from a simple observation: Singapore's heritage and arts scene
                                is incredibly vibrant, yet fragmented. Events were scattered across multiple platforms,
                                making it difficult for audiences to discover and engage with the rich tapestry of
                                performances, workshops and celebrations happening around them.
                            </p>
                            <p style={{ marginBottom: '20px' }}>
                                We recognized that both audiences and organizations were struggling. Audiences could not
                                easily find events that matched their interests, while arts and heritage organizations found it
                                challenging to reach their target communities effectively.
                            </p>
                            <p>
                                ATAM Connect bridges this gap by creating a unified platform where tradition meets
                                technology. We are not just listing events – we are building a community, preserving
                                heritage, and ensuring that Singapore's diverse arts and cultural expressions continue to thrive for generations to come.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                padding: '80px 0',
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                borderRadius: '24px',
                margin: '0 24px 64px'
            }}>
                <div className="container" style={{ textAlign: 'center' }}>
                    <h2 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        marginBottom: '16px',
                        color: 'white'
                    }}>
                        Join Our Community
                    </h2>
                    <p style={{
                        color: 'rgba(255,255,255,0.9)',
                        marginBottom: '32px',
                        fontSize: '18px',
                        maxWidth: '600px',
                        margin: '0 auto 32px'
                    }}>
                        Be part of Singapore's vibrant cultural community. Discover events,
                        connect with artists, and celebrate our rich heritage.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/events" className="btn" style={{
                            background: 'white',
                            color: 'var(--primary)',
                            fontWeight: '600'
                        }}>
                            Browse Events <ArrowRight size={18} />
                        </Link>
                        <Link to="/contact" className="btn" style={{
                            background: 'transparent',
                            border: '2px solid white',
                            color: 'white'
                        }}>
                            Partner With Us
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
